'use client';

import a__default, { memo, useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { classNames } from "../../utils/styles/classNames.js";
import { cloneWithProps } from "../../utils/props/cloneWithProps.js";
import { MenuItem } from "../../atoms/MenuItem/MenuItem.js";
import { wrapper, hidden, portal, menu } from "./ActionMenu.css.js";
import { useFloatingUi } from "./utils/useFloatingUi.js";
import { defaultProps } from "./ActionMenu.defaultProps.js";
import { useActionMenuItems } from "./utils/useActionMenuItems.js";
import { handleItemKeyDown } from "./utils/handleItemKeyDown.js";
import { handleTriggerKeyDown } from "./utils/handleTriggerKeyDown.js";
import { validateEmphasis } from "./utils/validateEmphasis.js";
import { validateSize } from "./utils/validateSize.js";
import { ActionMenuTrigger } from "./ActionMenu.Trigger.js";
function ActionMenuComponent({
  a11yLabel: a11yLabelProp,
  direction: directionProp,
  emphasis: emphasisProp,
  children: childrenProp,
  size: sizeProp,
  dataQa,
  items,
  className,
  onClose,
  onOpen
}) {
  const a11yLabel = a11yLabelProp || defaultProps.a11yLabel;
  const direction = directionProp || defaultProps.direction;
  const emphasis = validateEmphasis(emphasisProp) || defaultProps.emphasis;
  const size = validateSize(sizeProp) || defaultProps.size;
  const triggerRef = useRef(null);
  const currentItemRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const {
    dropdownProps: { wrapperRef, ...dropdownProps },
    triggerProps
  } = useFloatingUi({ isOpen, setIsOpen, triggerRef, direction });
  const {
    currentIndex,
    setCurrentIndex,
    interactiveItemsLength,
    firstInteractiveIndex,
    nextItemIndex,
    previousItemIndex
  } = useActionMenuItems(items);
  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => {
      var _a;
      return (_a = currentItemRef.current) == null ? void 0 : _a.focus();
    });
  }, [currentIndex, isOpen]);
  const handleClose = useCallback(() => {
    var _a;
    setIsOpen(false);
    if (onClose) onClose();
    (_a = triggerRef.current) == null ? void 0 : _a.focus();
  }, [triggerRef]);
  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        if (onOpen) onOpen();
      } else {
        handleClose();
      }
    },
    [isOpen]
  );
  const onItemKeyDown = useCallback(
    (event) => {
      handleItemKeyDown(event, {
        interactiveItemsLength,
        currentIndex,
        firstInteractiveIndex,
        handleClose,
        nextItemIndex,
        previousItemIndex,
        setCurrentIndex
      });
    },
    [interactiveItemsLength, currentIndex]
  );
  const onTriggerKeyDown = useCallback(
    (event) => {
      handleTriggerKeyDown(event, {
        currentItemRef,
        firstInteractiveIndex,
        interactiveItemsLength,
        setCurrentIndex,
        isOpen,
        setIsOpen
      });
    },
    [interactiveItemsLength, currentItemRef, isOpen]
  );
  let children = childrenProp;
  const triggerCommonProps = {
    ...triggerProps,
    onClick: handleClick,
    onKeyDown: onTriggerKeyDown,
    "data-qa": dataQa,
    "aria-haspopup": true,
    "aria-expanded": isOpen ? true : void 0,
    pressed: isOpen
  };
  if (childrenProp && triggerRef) {
    children = cloneWithProps(childrenProp, triggerCommonProps);
  } else {
    children = /* @__PURE__ */ a__default.createElement(
      ActionMenuTrigger,
      {
        ...triggerCommonProps,
        emphasis,
        size
      },
      a11yLabel
    );
  }
  return /* @__PURE__ */ a__default.createElement("div", { className: classNames(className, wrapper) }, children, createPortal(
    /* @__PURE__ */ a__default.createElement(
      "div",
      {
        ...dropdownProps,
        className: classNames(portal, !isOpen && hidden),
        ref: wrapperRef
      },
      /* @__PURE__ */ a__default.createElement("ul", { className: classNames(menu), role: "menu" }, items && items.map((item, index) => {
        const active = !item.disabled && index === currentIndex;
        const handleClickItem = (event) => {
          var _a;
          (_a = item.onClick) == null ? void 0 : _a.call(item, event);
          handleClose();
        };
        return /* @__PURE__ */ a__default.createElement(
          MenuItem,
          {
            ...item,
            key: item.label,
            ref: active ? currentItemRef : void 0,
            role: "menuitem",
            tabIndex: active ? void 0 : -1,
            onClick: handleClickItem,
            onKeyDown: onItemKeyDown
          }
        );
      }))
    ),
    document.body
  ));
}
const ActionMenu = /* @__PURE__ */ memo(ActionMenuComponent);
export {
  ActionMenu
};
